import { z } from 'zod';
import type { ChatMessage, CodexNotification } from '@reados/contracts';
import { sourceRangeSchema } from './selection';
import { annotationHighlightColor } from './annotation-accent';

const annotationSchema = z.object({
  id: z.string().uuid().refine(value => value[14] === '4').optional(),
  sourceThreadId: z.string().min(1).max(200), sourceMessageId: z.string().min(1).max(200),
  selectedText: z.string().min(1).max(32_000).refine(value => !!value.trim()),
  sourceRanges: z.array(sourceRangeSchema).min(1).max(128).optional(),
  groupId: z.string().uuid().optional(),
  comment: z.string().max(16_000).optional(),
}).strict();
const annotationsSchema = z.array(annotationSchema).max(20)
  .refine(items => items.reduce((total, item) => total + item.selectedText.length, 0) <= 128_000)
  .refine(items => items.reduce((total, item) => total + (item.comment?.length || 0), 0) <= 64_000)
  .refine(items => { const ids = items.flatMap(item => item.id ? [item.id.toLowerCase()] : []); return ids.length === new Set(ids).size; });

function presentAnnotations(user: ChatMessage, entries: z.infer<typeof annotationsSchema>) {
  const highlightColor = annotationHighlightColor(entries);
  const selections = entries.map((item, index) => ({
    id: item.id?.toLowerCase() || `reados-annotation:${JSON.stringify([user.threadId, user.turnId, index, item.sourceThreadId, item.sourceMessageId])}`,
    selectedText: item.selectedText,
    sourceKind: 'conversation', sourceDisplayName: '对话摘录',
    sourceMessageID: item.sourceMessageId, sourceThreadID: item.sourceThreadId,
    highlightColor,
    ...(item.sourceRanges ? { sourceRanges: item.sourceRanges.map(range => ({ startUTF16Offset: range.start, utf16Length: range.end - range.start })) } : {}),
  }));
  return { selections, annotations: selections.map((selection, index) => ({
    id: selection.id, text: selection.selectedText, highlightColor: selection.highlightColor,
    sourceMessageID: selection.sourceMessageID,
    source: { message_id: selection.sourceMessageID, thread_id: selection.sourceThreadID },
    ...(entries[index].comment?.trim() ? { annotation: entries[index].comment!.trim() } : {}),
  })) };
}

/** Server-validated, persisted SSE sideband. Native turn IDs are the join key;
 * arrival order and selected text must never be used to guess a user message.
 * Before that receipt arrives, the Web composer may present its exact submitted
 * snapshot on its own optimistic user row. This is local display metadata, not
 * a native event or source-authority receipt; persisted sideband takes priority. */
export function projectResponseAnnotations(messages: ChatMessage[], events: CodexNotification[]) {
  const result = new Map<ChatMessage, { selections: Record<string, unknown>[]; annotations: Record<string, unknown>[] }>();
  for (const user of messages) {
    if (user.role !== 'user' || typeof user.submissionId !== 'string' || !user.submissionId
      || user.readexWebPresentationID !== `pending-user:${user.submissionId}`) continue;
    const parsed = annotationsSchema.safeParse(user.readexWebResponseAnnotations);
    if (parsed.success) result.set(user, presentAnnotations(user, parsed.data));
  }
  for (const event of events) {
    if (event.method !== 'reados/responseAnnotations') continue;
    const params = event.params as Record<string, unknown> | undefined;
    if (!params || typeof params.turnId !== 'string' || !params.turnId) continue;
    const parsed = annotationsSchema.safeParse(params.responseAnnotations);
    if (!parsed.success) continue;
    const users = messages.filter(message => message.role === 'user' && message.turnId === params.turnId
      && (typeof params.threadId !== 'string' || message.threadId === params.threadId));
    // This sideband belongs to one thread's authenticated SSE channel. If a
    // caller combines ambiguous native identities, do not attach it elsewhere.
    if (!users.length || new Set(users.map(message => message.threadId)).size > 1) continue;
    const user = users[0];
    result.set(user, presentAnnotations(user, parsed.data));
  }
  return result;
}
